import './index.css';

import classNames from 'classnames';
import React, { useRef } from 'react';

import { useDateSegment } from '@react-aria/datepicker';
import { useFocusManager } from '@react-aria/focus';
import { usePress } from '@react-aria/interactions';
import type { PressHookProps } from '@react-aria/interactions';
import type { PressEvent } from '@react-types/shared';

import type { DateFieldState, DateSegment } from '@react-stately/datepicker';

import type { DatePickerBase, DateValue } from '@react-types/datepicker';

import { getDateTimeFormat } from '../../utils';

interface DatePickerSegmentProps extends DatePickerBase<DateValue> {
  id?: string;
  segment: DateSegment;
  state: DateFieldState;
}

export const DatePickerSegment: React.FC<DatePickerSegmentProps> = ({
  segment,
  state,
  ...otherProps
}) => {
  switch (segment.type) {
    // A separator, e.g. punctuation
    case 'literal':
      return <LiteralSegment segment={segment} />;

    // Editable segment
    default:
      return <EditableSegment segment={segment} state={state} {...otherProps} />;
  }
};

interface LiteralSegmentProps {
  segment: DateSegment;
}

const LiteralSegment: React.FC<LiteralSegmentProps> = ({ segment }) => {
  const focusManager = useFocusManager();

  const onPressStart = React.useCallback((e: PressEvent) => {
    if (e.pointerType === 'mouse') {
      let res = focusManager.focusNext({ from: e.target as HTMLElement });
      if (!res) {
        return focusManager.focusPrevious({ from: e.target as HTMLElement });
      }
    }
  }, []);

  const basePressProps = React.useMemo<PressHookProps>(() => {
    return {
      onPressStart: onPressStart
    };
  }, []);

  const { pressProps } = usePress(basePressProps);

  return (
    <span
      aria-hidden="true"
      className={classNames('datepicker-literal-style')}
      {...pressProps}
      data-testid={'literal'}
    >
      {segment.text}
    </span>
  );
};

const EditableSegment: React.FC<DatePickerSegmentProps> = ({ segment, state, ...otherProps }) => {
  const ref = useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  const className = React.useMemo<string>(() => {
    return classNames('datePicker-cell', {
      'is-placeholder': segment.isPlaceholder,
      'is-read-only': !segment.isEditable
    });
  }, [segment.isEditable, segment.isPlaceholder]);

  const content = React.useMemo<string>(() => {
    return segment.isPlaceholder ? getDateTimeFormat(segment.type) : segment.text;
  }, [segment.isPlaceholder, segment.type, segment.text]);

  const style = React.useMemo(() => {
    const s: React.CSSProperties = {};

    if (!state.value) {
      s.minWidth = '3ch';
    }

    return s;
  }, [state.value]);

  return (
    <div
      ref={ref}
      className={className}
      style={style}
      data-testid={`${segment.type}-${otherProps.id}`}
      {...segmentProps}
    >
      {content}
    </div>
  );
};
